import { useNavigate } from "@tanstack/react-router";
import { FiChevronRight, FiHome, FiTrash2 } from "react-icons/fi";
import type { MediaType } from "../../../generated/graphql";
import { useAppDispatch, useAppSelector } from "../../../store";
import { navigateToFolder } from "../../../store/slices/mediaSlice";

interface BreadcrumbsProps {
  currentFolderId: string | null;
  mediaType: MediaType;
  rootLabel?: string;
  showTrash?: boolean;
}

export function Breadcrumbs({
  mediaType,
  rootLabel = "Root",
  showTrash = false,
}: BreadcrumbsProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const folderPath = useAppSelector((state) => state.media.folderPath);

  // Helper to get media route based on type
  const getMediaRoute = (type: MediaType): string => {
    const typeMap: Record<MediaType, string> = {
      audio: "/media/audio",
      image: "/media/images",
      video: "/media/videos",
      file: "/media/files",
    };
    return typeMap[type];
  };

  const handleNavigate = (folderId: string | null, folderName: string, pathIndex: number) => {
    // If navigating from trash view, go back to folder view
    if (showTrash) {
      navigate({ to: getMediaRoute(mediaType) as string });
      return;
    }

    if (folderId === null) {
      // Navigating to root (no folder selected)
      dispatch(
        navigateToFolder({
          folderId: null,
          folderName: rootLabel,
          parentPath: [],
        })
      );
    } else {
      // Navigating to a folder in the breadcrumb path
      // pathIndex is the index in breadcrumbPath (which includes root at index 0)
      // We need to adjust for the root element: folderPath index = pathIndex - 1
      // Get the parent path up to this folder (excluding this folder itself)
      const folderPathIndex = pathIndex - 1;
      const parentPath = folderPath.slice(0, folderPathIndex);
      dispatch(navigateToFolder({ folderId, folderName, parentPath }));
    }
  };

  // Build breadcrumb path
  // If showing trash: root label + current folder path + "Trash"
  // Otherwise: root label + current folder path
  const breadcrumbPath = showTrash
    ? [{ id: null, name: rootLabel }, ...folderPath, { id: "trash", name: "Trash" }]
    : [{ id: null, name: rootLabel }, ...folderPath];

  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex items-center space-x-2 text-sm">
        {breadcrumbPath.map((crumb, index) => {
          const isLast = index === breadcrumbPath.length - 1;
          const isFirst = index === 0;
          const isTrashCrumb = crumb.id === "trash";

          return (
            <li key={crumb.id || `crumb-${index}`} className="flex items-center">
              {index > 0 && (
                <FiChevronRight className="mx-2 h-4 w-4 text-gray-400" aria-hidden="true" />
              )}
              {isLast ? (
                <span className="font-medium text-gray-900" aria-current="page">
                  {isFirst && <FiHome className="mr-1 inline h-4 w-4" aria-hidden="true" />}
                  {isTrashCrumb && <FiTrash2 className="mr-1 inline h-4 w-4" aria-hidden="true" />}
                  {crumb.name}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => handleNavigate(crumb.id, crumb.name, index)}
                  className="flex items-center text-gray-600 hover:text-gray-900 hover:underline transition-colors"
                >
                  {isFirst && <FiHome className="mr-1 h-4 w-4" aria-hidden="true" />}
                  {isTrashCrumb && <FiTrash2 className="mr-1 h-4 w-4" aria-hidden="true" />}
                  {crumb.name}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
